import { useState } from "react"
import { useWalletActions } from "@/wallet/actions"
import { formSchema as amountFormSchema } from "./send/send-amount.schema"
import { z } from "zod"
import { toast } from "sonner"
import Loader from "@/components/layout/loader"
import { useTransactionActions } from "@/api/transactions"
import SendRecipientForm from "./send/send-recipient.form"
import { formSchema as recipientFormSchema } from "./send/send-recipient.schema"
import SendAmountForm from "./send/send-amount.form"
import { Network, Token } from "@/config/tokens"
import { isValidEthereumAddress } from "@/lib/web3"
import SendPreviewPage from "./send/send-preview.page"

interface SendFlowProps {
  onStart: () => void;
  onDone: () => void;
}

type PreviewData = z.infer<typeof amountFormSchema>;

export default function SendFlow({ onDone, onStart }: SendFlowProps) {
  const [step, setStep] = useState<
    "recipient" | "amount" | "preview" | "process"
  >("recipient")
  const { makePayment } = useWalletActions()
  const [message, setMessage] = useState<string>("")
  const { saveTransaction } = useTransactionActions()
  const [address, setAddress] = useState<string | undefined>()
  const [wallet, setWallet] = useState<
    { ethAddress: string; solAddress: string; name?: string } | undefined
  >()
  const [networks, setNetworks] = useState<Network[]>([])
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)

  const onRecipientSubmit = async ({
    address,
    wallet: recipientWalletInfo,
  }: z.infer<typeof recipientFormSchema>) => {
    if (address) {
      setAddress(address)
      setWallet(undefined)
      setNetworks(
        isValidEthereumAddress(address)
          ? Object.values(Network).filter(
              (network) => network !== Network.SOLANA,
            )
          : [Network.SOLANA],
      )
    } else if (recipientWalletInfo) {
      setWallet(recipientWalletInfo)
      setAddress(undefined)
      setNetworks(Object.values(Network))
    }
    setStep("amount")
  }

  const handleAmountSubmit = (data: PreviewData) => {
    setPreviewData(data)
    setStep("preview")
  }

  const executeTransaction = async () => {
    if (!previewData) {
      toast.error("Transaction details are missing.")
      setStep("amount")
      return
    }

    const { token, network, amount } = previewData
    const destinationAddress = wallet
      ? network === Network.SOLANA
        ? wallet.solAddress
        : wallet.ethAddress
      : address

    if (!destinationAddress) {
      toast.error("No destination address found.")
      setStep("recipient")
      return
    }

    onStart()
    setStep("process")
    setMessage("Sending...")
    const { explorerLink, transactionId } = await makePayment(
      token,
      network,
      Number(amount),
      destinationAddress,
      (msg) => setMessage(msg),
    )
    setMessage("Updating...")
    await saveTransaction(
      network,
      destinationAddress,
      token,
      Number(amount),
      transactionId,
    )
    toast.success("Send succeeded", {
      action: { label: "View", onClick: () => window.open(explorerLink) },
    })
    setStep("recipient")
    onDone()
  }

  if (step === "recipient") {
    return <SendRecipientForm onSubmit={onRecipientSubmit} />
  } else if (step === "amount") {
    return (
      <SendAmountForm
        onBack={() => setStep("recipient")}
        onSubmit={handleAmountSubmit}
        networks={networks}
      />
    )
  } else if (step === "preview" && previewData) {
    const currentRecipientAddress = wallet
      ? previewData.network === Network.SOLANA
        ? wallet.solAddress
        : wallet.ethAddress
      : address
    return (
      <SendPreviewPage
        recipientAddress={currentRecipientAddress}
        recipientWalletName={wallet?.name}
        amount={previewData.amount}
        token={previewData.token as Token}
        network={previewData.network}
        onConfirm={executeTransaction}
        onBack={() => setStep("amount")}
      />
    )
  } else if (step === "process") {
    return <Loader message={message} />
  }
  return null
}
